import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { LuMail, LuPrinter, LuTrash2, LuUserCheck, LuUserX, LuShield } from "react-icons/lu";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { User, UserRole } from "../../api/types";
import { GenericTable, TableColumn } from "../ui/table";
import useFetchUsers from "./hooks/useFetchUsers";
import useSetAsMember from "./hooks/useSetAsMember";
import useSetAsNonMember from "./hooks/useSetAsNonMember";
import useDeleteUsers from "./hooks/useDeleteUsers";
import useUpdateRole, { getRoleLabel } from "./hooks/useUpdateRole";
import useShowToast from "../../hooks/useShowToast";
import api from "../../api";
import useAuthStore from "../../store/auth";

const ClubManagementUsers = (): ReactElement => {
  const { t } = useTranslation();
  const { users, loading, refetchUsers } = useFetchUsers();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const { setAsMember } = useSetAsMember();
  const { setAsNonMember } = useSetAsNonMember();
  const { deleteUsers } = useDeleteUsers();
  const { updateRole } = useUpdateRole();
  const { successToast, errorToast } = useShowToast();
  const [user] = useAuthStore(state => [state.user]);

  const completeColumns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.users.columns.surname"),
        key: "surname",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.name"),
        key: "name",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.username"),
        key: "username",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.email"),
        key: "email",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.phone"),
        key: "phone",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.role"),
        key: "role",
        sortable: true,
        render: (value: unknown) => getRoleLabel(value as UserRole),
      },
      {
        label: t("club_management.users.columns.is_member"),
        key: "is_member",
        sortable: true,
        render: (value: unknown) => (
          <Text color={value ? "green.500" : "red.500"}>
            {value ? t("club_management.users.member.yes") : t("club_management.users.member.no")}
          </Text>
        ),
      },
      {
        label: t("club_management.users.columns.signup_date"),
        key: "signup_date",
        sortable: true,
        render: (value: unknown) => formatDate(value as string | null),
      },
      {
        label: t("club_management.users.columns.last_booking_date"),
        key: "last_booking_date",
        sortable: true,
        render: (value: unknown) => formatDate(value as string | null),
      },
    ],
    [t],
  );

  const compactColumns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.users.columns.name"),
        key: "name",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.surname"),
        key: "surname",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.username"),
        key: "username",
        sortable: true,
      },
      {
        label: t("club_management.users.columns.is_member"),
        key: "is_member",
        sortable: true,
        render: (value: unknown) => (
          <Text color={value ? "green.500" : "red.500"}>
            {value ? t("club_management.users.member.yes") : t("club_management.users.member.no")}
          </Text>
        ),
      },
    ],
    [t],
  );

  const columns = isCompactView ? compactColumns : completeColumns;

  const handleRowSelectionChange = useCallback((selectedRows: unknown) => {
    const userIds = (selectedRows as User[]).map(row => row.id);
    setSelectedUserIds(userIds);
  }, []);

  const handleSetAsMember = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    setIsActionLoading(true);
    await setAsMember(selectedUserIds);
    setIsActionLoading(false);
    refetchUsers();
    setSelectedUserIds([]);
  };

  const handleSetAsNonMember = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    setIsActionLoading(true);
    await setAsNonMember(selectedUserIds);
    setIsActionLoading(false);
    refetchUsers();
    setSelectedUserIds([]);
  };

  const handleDeleteUsers = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    setIsActionLoading(true);
    await deleteUsers(selectedUserIds);
    setIsActionLoading(false);
    refetchUsers();
    setSelectedUserIds([]);
  };

  const handleSendEmail = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    const emails = users.filter(user => selectedUserIds.includes(user.id)).map(user => user.email);
    navigator.clipboard.writeText(emails.join(","));
    successToast({
      title: t("club_management.users.send_email.success_title"),
      description: t("club_management.users.send_email.emails_copied"),
    });
  };

  const handleUpdateRole = async (role: UserRole) => {
    if (selectedUserIds.length === 0) {
      return;
    }

    setIsActionLoading(true);
    await updateRole(selectedUserIds, role);
    setIsActionLoading(false);
    refetchUsers();
    setSelectedUserIds([]);
  };

  const handlePrintUsers = async () => {
    setIsActionLoading(true);
    await api.printUsersList(isCompactView);
    if (api.error) {
      errorToast({
        title: t("club_management.users.print.error_title"),
        description: t("club_management.users.print.error_description"),
      });
    }
    setIsActionLoading(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.users.title")}</Heading>
        <HStack spacing={3}>
          <FormControl display="flex" alignItems="center" width="auto">
            <FormLabel htmlFor="compact-view" mb={0} mr={2}>
              {t("club_management.users.compact_view")}
            </FormLabel>
            <Switch
              id="compact-view"
              isChecked={isCompactView}
              onChange={e => setIsCompactView(e.target.checked)}
            />
          </FormControl>
          <Button
            leftIcon={<LuPrinter />}
            colorScheme="blue"
            size="sm"
            onClick={handlePrintUsers}
            isLoading={isActionLoading}
          >
            {t("club_management.users.actions.print")}
          </Button>
        </HStack>
      </Box>

      {/* Actions */}
      {selectedUserIds.length > 0 && (
        <Box
          p={4}
          bg="gray.50"
          borderRadius="md"
          position="sticky"
          top={0}
          zIndex={10}
          boxShadow="sm"
        >
          <Text mb={3} fontWeight="medium">
            {selectedUserIds.length} {t("club_management.users.selected_users")}
          </Text>
          <HStack spacing={3}>
            <Button
              leftIcon={<LuUserCheck />}
              colorScheme="green"
              size="sm"
              onClick={handleSetAsMember}
              isLoading={isActionLoading}
            >
              {t("club_management.users.actions.set_as_member")}
            </Button>
            <Button
              leftIcon={<LuUserX />}
              colorScheme="orange"
              size="sm"
              onClick={handleSetAsNonMember}
              isLoading={isActionLoading}
            >
              {t("club_management.users.actions.set_as_non_member")}
            </Button>
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<LuShield />}
                colorScheme="purple"
                size="sm"
                isLoading={isActionLoading}
              >
                {t("club_management.users.actions.update_role")}
                <ChevronDownIcon />
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => handleUpdateRole(UserRole.USER)}>
                  {getRoleLabel(UserRole.USER)}
                </MenuItem>
                <MenuItem onClick={() => handleUpdateRole(UserRole.ADMIN)}>
                  {getRoleLabel(UserRole.ADMIN)}
                </MenuItem>
                <MenuItem onClick={() => handleUpdateRole(UserRole.MASTER)}>
                  {getRoleLabel(UserRole.MASTER)}
                </MenuItem>
                <MenuItem onClick={() => handleUpdateRole(UserRole.MANAGER)}>
                  {getRoleLabel(UserRole.MANAGER)}
                </MenuItem>
              </MenuList>
            </Menu>
            <Button
              leftIcon={<LuMail />}
              colorScheme="blue"
              size="sm"
              onClick={handleSendEmail}
              isLoading={isActionLoading}
            >
              {t("club_management.users.actions.send_email")}
            </Button>
            <Button
              leftIcon={<LuTrash2 />}
              colorScheme="red"
              size="sm"
              onClick={handleDeleteUsers}
              isLoading={isActionLoading}
            >
              {t("club_management.users.actions.delete")}
            </Button>
          </HStack>
        </Box>
      )}

      {/* Users Table */}
      <GenericTable
        data={users as unknown as Record<string, unknown>[]}
        columns={columns}
        enableRowSelection={user?.role === UserRole.MASTER}
        onRowSelectionChange={handleRowSelectionChange}
        size="sm"
      />
    </VStack>
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) {
    return "-";
  }
  try {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: it });
  } catch {
    return "-";
  }
};

export default ClubManagementUsers;
